import React, { useState } from "react";
import { useNavigate } from "react-router";
import { useCreateVariable } from "../../hooks/useVariables";
import { VariableCreate } from "../../types/variable";
import { Input, Textarea } from "../../components/ui/Input";

const CreateVariablePage: React.FC = () => {
  const navigate = useNavigate();
  const createMutation = useCreateVariable();
  const [form, setForm] = useState<VariableCreate>({
    var_name: "",
    description: "",
    sql_query: "",
    db_name: "",
    transform: "",
    cache_expiration_minutes: undefined,
    not_expire: false,
    http_request_endpoint_id: undefined,
    endpoint_field: "",
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field: keyof VariableCreate, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload: VariableCreate = {
        var_name: form.var_name.trim(),
        description: form.description || undefined,
        sql_query: form.sql_query || undefined,
        db_name: form.db_name || undefined,
        transform: form.transform || undefined,
        cache_expiration_minutes: form.cache_expiration_minutes ?? undefined,
        not_expire: !!form.not_expire,
        http_request_endpoint_id: form.http_request_endpoint_id ?? undefined,
        endpoint_field: form.endpoint_field || undefined,
      };
      const created = await createMutation.mutateAsync(payload);
      navigate(`/variables/${created.id}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Create Variable</h2>
        <p className="text-sm text-gray-500">Define a new template variable</p>
      </div>

      <form
        className="bg-white shadow sm:rounded-lg p-6 space-y-4"
        onSubmit={handleSubmit}
      >
        <Input
          label="Name"
          type="text"
          value={form.var_name}
          onChange={(e) => handleChange("var_name", e.target.value)}
          required
          variant="rounded-md"
        />
        <Textarea
          label="Description"
          rows={3}
          value={form.description || ""}
          onChange={(e) => handleChange("description", e.target.value)}
          variant="rounded-md"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="DB Name"
            type="text"
            value={form.db_name || ""}
            onChange={(e) => handleChange("db_name", e.target.value)}
            variant="rounded-md"
          />
          <Input
            label="Transform"
            type="text"
            value={form.transform || ""}
            onChange={(e) => handleChange("transform", e.target.value)}
            placeholder="pgp_decrypt, aes256_decrypt"
            variant="rounded-md"
          />
        </div>
        <Textarea
          label="SQL Query"
          rows={6}
          value={form.sql_query || ""}
          onChange={(e) => handleChange("sql_query", e.target.value)}
          placeholder="SELECT ... WHERE member_id = :member_id"
          variant="rounded-md"
          className="font-mono"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Cache Expiration (minutes)"
            type="number"
            min={0}
            value={form.cache_expiration_minutes ?? ""}
            onChange={(e) =>
              handleChange(
                "cache_expiration_minutes",
                e.target.value ? Number(e.target.value) : undefined,
              )
            }
            variant="rounded-md"
          />
          <div className="flex items-center mt-6">
            <input
              id="not_expire"
              type="checkbox"
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
              checked={!!form.not_expire}
              onChange={(e) => handleChange("not_expire", e.target.checked)}
            />
            <label
              htmlFor="not_expire"
              className="ml-2 block text-sm text-gray-700"
            >
              Not expire (cache indefinitely)
            </label>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="HTTP Endpoint ID"
            type="number"
            value={form.http_request_endpoint_id ?? ""}
            onChange={(e) =>
              handleChange(
                "http_request_endpoint_id",
                e.target.value ? Number(e.target.value) : undefined,
              )
            }
            variant="rounded-md"
          />
          <Input
            label="Endpoint Field"
            type="text"
            value={form.endpoint_field || ""}
            onChange={(e) => handleChange("endpoint_field", e.target.value)}
            placeholder="user.name or data[0].id"
            variant="rounded-md"
          />
        </div>

        <div className="flex justify-end space-x-2">
          <button
            type="button"
            className="btn-secondary px-4 py-2 rounded-md"
            onClick={() => navigate("/variables")}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary px-4 py-2 rounded-md"
          >
            {saving ? "Saving..." : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateVariablePage;
